class CollectionGridLayout {
  constructor() {
    this.init();
  }

  init() {
    // Ejecutar cuando el DOM esté listo
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.applyProductFormats());
    } else {
      this.applyProductFormats();
    }

    // Re-evaluar cuando se carguen nuevos productos (infinite scroll)
    window.addEventListener('infiniteScroll:loaded', () => {
      this.applyProductFormats();
    });

    // Re-evaluar cuando se eliminen banners duplicados
    window.addEventListener('collection:layoutChanged', () => {
      this.applyProductFormats();
    });

    // Re-evaluar cuando se apliquen filtros
    document.addEventListener('facets:change', () => {
      // Usar setTimeout para esperar a que el DOM se actualice completamente
      setTimeout(() => {
        // Re-configurar el observer porque el DOM se reemplazó
        this.observeGridChanges();
        // Aplicar formatos
        this.applyProductFormats();
      }, 200);
    });

    // Editor de temas: Shopify repinta la sección entera al tocar un ajuste, así
    // que el #product-grid que observamos queda descolgado y el nuevo llega sin
    // formatos aplicados (ocupación, huérfano, filas de banner). Mismo par que el
    // de facets:change, que es el otro momento en el que el DOM se reemplaza
    // entero.
    //
    // Quien vuelve a revelar esa parrilla es collection-section.js, escuchando
    // este mismo evento, y el orden de los dos <script> no es el mismo en la PLP
    // que en la plantilla de shop the look: el setTimeout deja que corran los dos
    // listeners antes de tocar el marcado nuevo, venga primero el que venga.
    document.addEventListener('shopify:section:load', (event) => {
      if (!event.target.querySelector('#product-grid')) return;
      setTimeout(() => {
        this.observeGridChanges();
        this.applyProductFormats();
      }, 0);
    });

    // Observer para detectar cambios en data-columns
    this.observeGridChanges();

    // Re-evaluar cuando se redimensiona la ventana (móvil <-> desktop)
    let resizeTimer;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.applyProductFormats();
      }, 250);
    });
  }

  /**
   * Detecta items "huérfanos" que quedan solos en una fila.
   * Casos detectados (móvil y desktop - los formatos dobles aplican en ambos):
   * 1. Producto simple seguido de producto doble (mala configuración en backoffice)
   * 2. Producto simple + producto OOS + producto doble (OOS oculto deja hueco)
   * 3. Producto OOS + producto simple + producto doble (OOS a la izquierda)
   *
   * El último producto solo en su fila no se detecta aquí: lo hace markLastOrphan,
   * en todas las vistas.
   *
   * @param {Array} allItems - Todos los items del grid (incluyendo OOS)
   * @param {Array} visibleItems - Solo los items visibles (sin OOS)
   */
  detectOrphanItems(allItems, visibleItems) {
    const orphanItems = new Set();

    // Función para determinar si un formato ocupa toda la fila (es "doble")
    // NOTA: Estos formatos usan grid-column: 1 / -1 tanto en móvil como en desktop
    const isDoubleFormat = (format) => {
      return format === 'panoramica_actual' ||
        format === 'panoramica_grande' ||
        format === 'vertical_grande';
    };

    // Función para obtener el formato original de un item (sin modificaciones previas)
    const getOriginalFormat = (item) => {
      return item.dataset.originalFormat || item.getAttribute('data-format');
    };

    // Una tarjeta de "Shop the look" ocupa la fila entera en 2 columnas sea cual
    // sea su formato, así que cuenta como doble aunque no tenga data-format.
    const isFullRowItem = (item) => {
      if (!item) return false;
      if (item.classList.contains('collection-look-item')) return true;
      if (item.classList.contains('collection-row')) return true;
      return isDoubleFormat(getOriginalFormat(item));
    };

    // Casos 1, 2, 3: Aplican tanto en móvil como en desktop
    // (los formatos dobles usan grid-column: 1 / -1 en todas las resoluciones)

    // Recorrer items visibles y calcular su posición en el grid
    let currentColumn = 0; // 0 = izquierda, 1 = derecha

    for (let i = 0; i < visibleItems.length; i++) {
      const item = visibleItems[i];
      const isDouble = isFullRowItem(item);

      // Guardar la columna donde empieza este item
      const itemColumn = currentColumn;

      // Actualizar columna para el siguiente
      if (isDouble) {
        // Los dobles ocupan toda la fila, el siguiente empieza en columna 0
        currentColumn = 0;
      } else {
        // Los simples alternan
        currentColumn = currentColumn === 0 ? 1 : 0;
      }

      // Detectar huérfano: item simple en columna izquierda seguido de un doble
      if (!isDouble && itemColumn === 0 && isFullRowItem(visibleItems[i + 1])) {
        // Este item queda solo antes de un doble → huérfano
        orphanItems.add(item);
      }
    }

    // También detectar huérfanos causados por productos OOS
    // Recalcular considerando TODOS los items (incluyendo OOS)
    currentColumn = 0;
    const allItemsPositions = new Map();

    allItems.forEach((item) => {
      const isDouble = isFullRowItem(item);
      const isOOS = item.classList.contains('product-out-of-stock');

      allItemsPositions.set(item, { column: currentColumn, isDouble, isOOS });

      if (isDouble) {
        currentColumn = 0;
      } else {
        currentColumn = currentColumn === 0 ? 1 : 0;
      }
    });

    // Buscar casos donde un visible queda solo por OOS adyacente(s) + doble siguiente
    // Soporta múltiples OOS consecutivos entre el simple y el doble
    for (let i = 0; i < allItems.length; i++) {
      const item = allItems[i];
      const pos = allItemsPositions.get(item);

      // Saltar si es OOS o doble
      if (pos.isOOS || pos.isDouble) continue;

      // Buscar el siguiente item VISIBLE (saltando todos los OOS)
      let nextVisibleIndex = i + 1;
      while (nextVisibleIndex < allItems.length) {
        const nextItem = allItems[nextVisibleIndex];
        const nextPos = allItemsPositions.get(nextItem);
        if (!nextPos.isOOS) break; // Encontramos un visible
        nextVisibleIndex++;
      }

      const nextVisibleItem = allItems[nextVisibleIndex];
      if (!nextVisibleItem) continue;

      const nextVisiblePos = allItemsPositions.get(nextVisibleItem);

      // Contar cuántos OOS hay entre este item y el siguiente visible
      const oosCount = nextVisibleIndex - i - 1;

      // Si el siguiente visible es doble, verificar si este item queda huérfano
      if (nextVisiblePos.isDouble) {
        if (pos.column === 0) {
          // Simple en columna izquierda con OOS después
          if (oosCount > 0) {
            orphanItems.add(item);
          }
        } else if (pos.column === 1) {
          // Simple en columna derecha con OOS a la izquierda
          const prevItem = allItems[i - 1];
          if (prevItem) {
            const prevPos = allItemsPositions.get(prevItem);
            if (prevPos.isOOS) {
              orphanItems.add(item);
            }
          }
        }
      }
    }

    return orphanItems;
  }

  /**
   * Parrilla impar: el último elemento queda solo en su fila. Se centra, con el ancho de
   * una columna, en cualquier vista (2 y 4 en desktop, 1 y 2 en móvil).
   *
   * Se mide sobre el layout en vez de deducirlo de los formatos como detectOrphanItems:
   * qué va a fila completa (B17, looks, filas row_grid) y qué se oculta (agotados, banners
   * de la vista real) cambia de una vista a otra. Solo cuando ya no quedan productos por
   * cargar: hasta entonces ese no es el último.
   */
  markLastOrphan(grid) {
    const items = Array.from(grid.children).filter(
      (el) => el.classList.contains('collection__grid__item') || el.classList.contains('collection-row')
    );
    items.forEach((item) => item.classList.remove('collection__grid__item--last-orphan'));

    if (this.hasMoreProductsToLoad()) return;

    const gridStyle = window.getComputedStyle(grid);
    // Una sola columna: todos van solos y ya ocupan la fila.
    if (gridStyle.gridTemplateColumns.split(' ').length < 2) return;

    // Los ocultos (display: none) no tienen cajas.
    const shown = items.filter((item) => item.getClientRects().length > 0);
    const last = shown[shown.length - 1];
    if (!last) return;

    // Solo un item de una columna. Lo que lleva grid-column ya ocupa la fila entera.
    const lastStyle = window.getComputedStyle(last);
    if (lastStyle.gridColumnStart !== 'auto' || lastStyle.gridColumnEnd !== 'auto') return;

    // Comparte fila con el anterior: misma línea de arriba.
    const prev = shown[shown.length - 2];
    if (prev && last.getBoundingClientRect().top - prev.getBoundingClientRect().top < 1) return;

    // El CSS calcula el ancho de una columna con --cols; el gap entre columnas cambia
    // según vista y viewport, así que se toma el real.
    last.style.setProperty('--last-orphan-gap', `${parseFloat(gridStyle.columnGap) || 0}px`);
    last.classList.add('collection__grid__item--last-orphan');
  }

  /**
   * Loader visible (inline o por CSS). No usar solo style.display: sin inline,
   * el tema oculta .infinite-scroll-loading vía CSS y '' !== 'none' daba true a falsas.
   */
  isLoadingIndicatorVisible(loadingIndicator) {
    if (!loadingIndicator) return false;
    return window.getComputedStyle(loadingIndicator).display !== 'none';
  }

  /**
   * Verifica si hay más productos por cargar (paginación o infinite scroll)
   */
  hasMoreProductsToLoad() {
    const paginationWrapper = document.querySelector('.wt-pagination-wrapper');
    const loadingIndicator = document.querySelector('.infinite-scroll-loading');
    const grid = document.querySelector('#product-grid');

    // Determinar si infinite scroll está activo (paginación oculta)
    const paginationHidden = paginationWrapper && paginationWrapper.style.display === 'none';

    // CASO 1: Infinite scroll activo (paginación oculta)
    if (paginationHidden) {
      // El infinite scroll expone hasMorePages en el grid tras cada carga/paginación
      const hasMoreAttr = grid?.dataset.infiniteScrollHasMorePages;
      if (hasMoreAttr === 'false') return false;
      if (hasMoreAttr === 'true') return true;

      // Fallback: productos precargados, loading, o enlace "siguiente" en paginación oculta
      const preloadedHidden = document.querySelectorAll('.collection__grid__item.preloaded-hidden');
      if (preloadedHidden.length > 0 || this.isLoadingIndicatorVisible(loadingIndicator)) return true;
      if (paginationWrapper?.querySelector('.wt-pagination__item--prev, a[rel="next"]')) return true;

      return false;
    }

    // CASO 2: Paginación tradicional visible
    if (paginationWrapper) {
      const nextLink = paginationWrapper.querySelector('.wt-pagination__item--prev');
      if (nextLink) return true;
    }

    // Fallback: solo si el loader está realmente visible (p. ej. cargando más)
    if (this.isLoadingIndicatorVisible(loadingIndicator)) return true;

    return false;
  }

  observeGridChanges() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    // Desconectar observer anterior si existe
    if (this.gridObserver) {
      this.gridObserver.disconnect();
    }

    this.gridObserver = new MutationObserver((mutations) => {
      // Una sola vez por lote de mutaciones, aunque el toggle de origen haga
      // classList.remove() + classList.add() por separado (2 mutation records) — evita
      // forzar el relayout de abajo dos veces por un mismo click.
      let relayoutForced = false;

      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-columns') {
          // Usar setTimeout para asegurar que el cambio se haya aplicado completamente
          setTimeout(() => {
            this.applyProductFormats();
          }, 50);
        }
        // Cambio de vista-1/2/4 (selector de columnas): switch-column.js y el script inline
        // de esta sección mutan la clase directamente en 3 sitios distintos, ninguno dispara
        // un evento propio. Este observer ya escuchaba todos los atributos de este mismo
        // nodo, así que basta con reaccionar también a "class" para avisar de forma
        // centralizada (evita depender de tocar los 3 sitios si se añade un cuarto algún día).
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          // Si la clase anterior no tenía ya un vista-*, esta mutación es la primera
          // asignación de vista al cargar la página (initGridToggle(), vía DOMContentLoaded),
          // no un toggle real — no hay ningún alto cacheado de una vista previa que
          // invalidar todavía. Sin este guard, el relayout de abajo se disparaba SIEMPRE
          // en la carga inicial, forzando un reflow completo de toda la parrilla (incluidas
          // las cards fuera de viewport) justo en el camino crítico de renderizado, compitiendo
          // con el propio LCP.
          const hadVista = mutation.oldValue && mutation.oldValue.includes('vista-');
          if (hadVista && !relayoutForced) {
            relayoutForced = true;
            // Cambiar de vista cambia el ancho de cada card (1↔2↔4 columnas) y, por el
            // aspect-ratio fijo de las fotos, también su alto real. Pero el alto
            // "recordado" que usa content-visibility:auto (ver scss/pages/_collection.scss)
            // para las cards aún no vistas sigue siendo el de la vista anterior hasta que
            // se relayoutean de verdad — algo que solo pasa al hacerlas visibles (con
            // scroll), dejando huecos/alturas erróneas hasta entonces. Forzamos ese
            // relayout una vez aquí (clase que desactiva content-visibility un instante,
            // leer una métrica de layout para forzar el reflow, revertir) en vez de
            // esperar a que el usuario lo descubra haciendo scroll. Desconectar/reconectar
            // el propio observer alrededor: el toggle de la clase de abajo es en sí mismo
            // una mutación de "class" sobre este mismo nodo, y sin esto se retriggerearía
            // a sí mismo sin parar.
            this.gridObserver.disconnect();
            grid.classList.add('content-viz-relayout');
            void grid.offsetHeight;
            grid.classList.remove('content-viz-relayout');
            this.gridObserver.observe(grid, { attributes: true, attributeOldValue: true });

            // La vista móvil (1↔2) solo cambia esta clase, no data-columns, y cambia
            // qué ocupa la fila entera y qué se oculta: hay que volver a medir el último.
            this.markLastOrphan(grid);
          }

          document.dispatchEvent(new CustomEvent('collection:viewToggled'));
        }
      });
    });

    this.gridObserver.observe(grid, { attributes: true, attributeOldValue: true });
  }

  applyProductFormats() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    // Colocar los banners posicionados ANTES de leer los items, para que entren en el
    // cálculo de formatos/huérfanos en esta misma pasada. Es idempotente (omite los ya
    // colocados), así que es seguro reejecutarlo en cada evento que dispara este método
    // (carga inicial, infiniteScroll:loaded, facets:change, resize, cambio de columnas).
    this.placeBanners(grid);

    // Filas `row_grid`: agrupa cada banner con row_grid y sus N-1 elementos siguientes en
    // una <collection-row>. Tras placeBanners para que los banners recién colocados entren.
    this.groupRows(grid);

    // Los productos duplicados por banner (placeBanners) llevan su propia galería
    // alternativa anidada dentro de OTRO <template> (el de posicionamiento de banners) —
    // al clonarse e insertarse aquí arriba, llega igual de inerte. Este mismo punto sirve
    // de "resync" general: cubre tanto esos banners recién colocados como el grid entero
    // reemplazado tras un filtrado (facets:change sustituye #product-grid por HTML nuevo
    // con templates sin instanciar).
    if (window.altGalleryHelpers) window.altGalleryHelpers.instantiateIn(grid);

    const columns = grid.getAttribute('data-columns');

    // Obtener todos los items (incluyendo out-of-stock para detectar adyacencias).
    // Solo hijos directos: una fila `row_grid` cuenta como UN item de fila completa y lo
    // que lleva dentro no entra en el juego de formatos/huérfanos.
    const allItems = Array.from(grid.children).filter(
      (el) => el.classList.contains('collection__grid__item') || el.classList.contains('collection-row')
    );

    // Crear un mapa de índices: posición visible -> item
    const visibleItems = [];
    const outOfStockPositions = new Map(); // Guardar posición original de out-of-stock

    allItems.forEach((item, originalIndex) => {
      if (item.classList.contains('product-out-of-stock')) {
        outOfStockPositions.set(originalIndex, item);
      } else {
        visibleItems.push(item);
      }
    });

    // Guardar formato original si no existe (para poder recalcular).
    // Los looks quedan fuera: su ocupación la resuelve el CSS con data-look-format
    // y no deben entrar en el juego de data-format (que se reescribe al cambiar
    // de vista).
    allItems.forEach(item => {
      if (item.classList.contains('collection-look-item') || item.classList.contains('collection-row')) return;
      if (!item.dataset.originalFormat) {
        item.dataset.originalFormat = item.getAttribute('data-format') || 'vertical_actual';
      }
    });

    // Limpiar clases de huérfano anteriores
    allItems.forEach(item => {
      item.classList.remove('collection__grid__item--orphan');
    });

    // Solo aplicar en vista de 2 columnas
    if (columns !== '2') {
      // Limpiar todos los estilos inline en otras vistas
      allItems.forEach(item => {
        if (item.classList.contains('collection-row')) return;
        this.clearInlineStyles(item);
        // Restaurar formato original
        if (item.dataset.originalFormat) {
          item.setAttribute('data-format', item.dataset.originalFormat);
        }
      });
      this.markLastOrphan(grid);
      return;
    }

    const isDesktop = window.innerWidth > 768;

    // Detectar items huérfanos (OOS y dobles)
    const orphanItems = this.detectOrphanItems(allItems, visibleItems);

    // Aplicar clase de huérfano (centra sin cambiar tamaño)
    orphanItems.forEach(item => {
      item.classList.add('collection__grid__item--orphan');
      const title = item.querySelector('.brn-card__title')?.textContent?.trim() || 'Sin título';
      console.log('[GridLayout] Huérfano detectado:', title);
    });

    // Aplicar formatos según data-format (solo en desktop)
    if (isDesktop) {
      visibleItems.forEach((item) => {
        if (item.classList.contains('collection-row')) return;
        const format = item.dataset.originalFormat || item.getAttribute('data-format');

        // Si ya es huérfano, el CSS de --orphan tiene prioridad, no aplicar vertical_grande
        if (item.classList.contains('collection__grid__item--orphan')) {
          this.clearInlineStyles(item);
          return;
        }

        // Aplicar formato según data-format configurado en backoffice
        switch (format) {
          case 'vertical_grande':
            this.applyVerticalGrandeFormat(item);
            break;
          case 'panoramica_actual':
            this.applyPanoramicaActualFormat(item);
            break;
          case 'panoramica_grande':
            this.applyPanoramicaGrandeFormat(item);
            break;
          case 'vertical_actual':
          default:
            this.clearInlineStyles(item);
            break;
        }
      });
    }

    // Al final: mide el layout con los huérfanos y formatos de arriba ya aplicados.
    this.markLastOrphan(grid);
  }

  /**
   * Coloca los banners posicionados (collection.metafields.custom.banner) en el grid.
   *
   * Por qué aquí y no en Liquid: `banner.posicion` es un índice ABSOLUTO de slot visible
   * (la N-ésima card visible, contando productos visibles + banners). Dentro de la página N
   * de la paginación, Liquid no conoce el recuento real de slots de las páginas anteriores
   * (banners insertados antes + productos agotados ocultos), así que la antigua estimación
   * `(página-1)*page_size` se desviaba y rompía el orden, acumulándose con el infinite scroll.
   * Los banners se emiten UNA vez como <template> en #collection-banner-data y se colocan
   * aquí sobre el DOM ya concatenado, contando los slots visibles reales.
   *
   * Idempotente: un banner ya colocado existe como
   * `.collection-banner-item[data-banner-position]` en el grid, así que se omite. Al filtrar,
   * facets reemplaza el grid y #collection-banner-data, y se recolocan en la siguiente pasada.
   */
  placeBanners(grid) {
    const data = document.getElementById('collection-banner-data');
    if (!data) return;

    // Banners ocultos en este market (`hide_market`): Liquid no los emite, pero deja una
    // marca `.brn-banner-skipped` con su `posicion`. Las posiciones son slots ABSOLUTOS y
    // se numeraron contando también esos banners, así que hay que descontarlos: si no,
    // cada banner posterior caería un slot más abajo justo en los markets donde el
    // banner está oculto. El banner oculto no debe contar para nada.
    const skippedPositions = [];
    data.querySelectorAll('.brn-banner-skipped').forEach((el) => {
      const pos = parseInt(el.getAttribute('data-posicion'), 10);
      if (Number.isInteger(pos) && pos >= 1) skippedPositions.push(pos);
    });
    // Estrictamente menor: el banner oculto solo desplaza a los que van detrás.
    const shiftFor = (pos) => skippedPositions.reduce((n, p) => (p < pos ? n + 1 : n), 0);

    // Los looks son banners de producto con `shop_the_look` marcado, así que llegan como
    // una plantilla de banner más y comparten con ellos posiciones y colocación.
    const seen = new Set();
    const all = [];
    data.querySelectorAll('template.brn-banner-tpl').forEach((tpl) => {
      const pos = parseInt(tpl.getAttribute('data-posicion'), 10);
      if (!Number.isInteger(pos) || pos < 1) return;
      // Deduplicado por posicion (el primero gana).
      if (seen.has(pos)) return;
      seen.add(pos);
      all.push({ pos, tpl });
    });
    if (all.length === 0) return;

    all.sort((a, b) => a.pos - b.pos);

    const pending = [];
    all.forEach((entry) => {
      if (grid.querySelector(`.collection-banner-item[data-banner-position="${entry.pos}"]`)) return;

      const node = entry.tpl.content.firstElementChild;
      // `entry.pos` sigue siendo la posición cruda (la que lleva data-banner-position y con
      // la que se comprueba si ya está colocado); solo el slot de inserción se desplaza.
      const pos = Math.max(1, entry.pos - shiftFor(entry.pos));
      if (node) pending.push({ pos, node: node.cloneNode(true) });
    });

    // Se inserta en orden de slot efectivo (tras descontar los banners ocultos).
    pending.sort((a, b) => a.pos - b.pos);
    pending.forEach((item) => this.insertBannerAt(grid, item.pos, item.node));
  }

  /**
   * Inserta un banner como el slot visible `posicion` (1-based).
   * Reglas de slot (idénticas al render original): un banner SIEMPRE cuenta como slot; un
   * producto solo cuenta si está visible (sin product-out-of-stock); los productos precargados
   * aún ocultos (preloaded-hidden) no cuentan y frenan la colocación hasta que se revelen
   * (se reintenta en el siguiente infiniteScroll:loaded).
   * @returns {boolean} true si se insertó
   */
  insertBannerAt(grid, posicion, node) {
    const items = this.getFlatItems(grid);

    let slots = 0;
    for (const item of items) {
      // Punto de inserción: hay posicion-1 slots antes de este item. Puede caer dentro de
      // una fila `row_grid` (su padre es el track): groupRows saca luego lo que sobre.
      if (slots === posicion - 1) {
        item.parentNode.insertBefore(node, item);
        return true;
      }
      // Bloque de productos precargados aún ocultos: no colocar más allá de lo revelado.
      if (item.classList.contains('preloaded-hidden')) return false;
      if (
        item.classList.contains('collection-banner-item') ||
        !item.classList.contains('product-out-of-stock')
      ) {
        slots++;
      }
    }

    // Fin del contenido revelado, sin productos precargados pendientes, y la posición es
    // justo el siguiente slot tras el último visible: es el slot correcto, se anexa al final.
    // Si luego llegan más productos (scroll infinito), se añaden después del banner igualmente.
    if (slots === posicion - 1) {
      grid.appendChild(node);
      return true;
    }
    // Posición más allá del último slot real (p. ej. la posición contaba productos que la
    // web oculta por agotados, o la colección perdió productos después de crear el banner).
    // Si ya no quedan páginas por cargar, se clava al final en vez de no mostrarse nunca.
    if (slots < posicion - 1 && !this.hasMoreProductsToLoad()) {
      grid.appendChild(node);
      return true;
    }
    return false;
  }

  /**
   * Items del grid en orden, "aplanando" las filas `row_grid`: lo que va dentro de una
   * <collection-row> sigue contando como slots sueltos (las posiciones de los banners no
   * cambian por agrupar).
   */
  getFlatItems(grid) {
    const items = [];
    Array.from(grid.children).forEach((el) => {
      if (el.classList.contains('collection-row')) {
        const track = el.querySelector('.collection-row__track');
        if (track) items.push(...Array.from(track.children));
      } else if (el.classList.contains('collection__grid__item')) {
        items.push(el);
      }
    });
    return items;
  }

  /**
   * Filas `row_grid`: un elemento con data-row-grid=N (banner de `custom.banner` o card de
   * producto con `row_grid` en su `custom.grid_b17`) se muestra en la misma fila que los
   * N-1 elementos visibles que le siguen (fila estática o slider según tamaño/viewport, lo
   * decide el CSS con data-row-size). Se envuelven en una <collection-row> que ocupa la fila
   * entera del grid.
   *
   * Sin estado: cada pasada crea las filas de los banners aún sueltos y "normaliza" todas
   * (completa las que se quedaron cortas porque sus siguientes productos aún no se habían
   * cargado/revelado, y devuelve al grid lo que sobre si placeBanners metió un banner dentro).
   * No se anidan: un banner con row_grid que cae dentro de otra fila es un elemento más.
   *
   * En mobile una fila de 4 no se pinta: el elemento va como sin fila (igual que en la app y
   * en la vista mobile de brownie-tools). Como eso depende del viewport, al cruzar el
   * breakpoint se deshacen todas las filas y se vuelven a montar, para que quede igual que
   * una carga en frío en el viewport nuevo.
   */
  groupRows(grid) {
    const tpl = document.getElementById('collection-row-tpl');
    if (!tpl) return;

    // Mismo breakpoint que el CSS de .collection-row.
    const isMobile = window.matchMedia('(max-width: 767.98px)').matches;
    if (this._rowsMobile !== isMobile) {
      grid.querySelectorAll(':scope > .collection-row').forEach((row) => this.unwrapRow(row));
      this._rowsMobile = isMobile;
    }

    grid.querySelectorAll(':scope > .collection__grid__item[data-row-grid]').forEach((anchor) => {
      // Ya absorbido por la fila de un elemento anterior (se rellena en orden, abajo).
      if (anchor.parentNode !== grid) return;
      const size = parseInt(anchor.getAttribute('data-row-grid'), 10);
      // Elemento oculto (agotado) o precargado aún sin revelar: no arranca fila (el
      // precargado la arrancará en la pasada en la que se revele).
      if (!Number.isInteger(size) || size < 2 || !this.isRowVisible(anchor)) return;
      if (isMobile && size === 4) return;

      const row = tpl.content.firstElementChild.cloneNode(true);
      row.setAttribute('data-row-grid', String(size));
      grid.insertBefore(row, anchor);
      this.moveIntoRow(row, anchor);
      // Se rellena ya, antes de pasar al siguiente banner: si este cae dentro de su rango,
      // entra como elemento en vez de arrancar su propia fila.
      this.normalizeRow(row);
    });

    grid.querySelectorAll(':scope > .collection-row').forEach((row) => this.normalizeRow(row));
  }

  normalizeRow(row) {
    const track = row.querySelector('.collection-row__track');
    const size = parseInt(row.getAttribute('data-row-grid'), 10);
    if (!track || !Number.isInteger(size)) return;

    const visibleCount = () => Array.from(track.children).filter((el) => this.isRowVisible(el)).length;

    // Sobra algo (banner insertado dentro): se devuelve al grid justo detrás de la fila,
    // conservando el orden. Los agotados del final (ocultos) salen con él.
    while (visibleCount() > size) {
      let last = track.lastElementChild;
      while (last && !this.isRowVisible(last)) {
        row.after(last);
        this.restoreRowFormat(last);
        last = track.lastElementChild;
      }
      if (!last) break;
      row.after(last);
      this.restoreRowFormat(last);
    }

    // Falta algo: se tira de los siguientes hermanos. Para en productos precargados aún
    // ocultos (se completará en el siguiente infiniteScroll:loaded), en looks (ocupan la
    // fila entera) y en otra fila. Los agotados entran (van ocultos, no cuentan) para no
    // romper la contigüidad.
    let count = visibleCount();
    while (count < size) {
      const next = row.nextElementSibling;
      if (!next || !next.classList.contains('collection__grid__item')) break;
      if (next.classList.contains('preloaded-hidden') || next.classList.contains('collection-look-item')) break;
      this.moveIntoRow(row, next);
      if (this.isRowVisible(next)) count++;
    }

    row.setAttribute('data-row-size', String(count));
    row.style.setProperty('--row-size', String(Math.max(count, 1)));
    if (typeof row.refresh === 'function') row.refresh();
  }

  // Devuelve al grid, en su sitio y en orden, todo lo que lleva la fila (ocultos incluidos).
  unwrapRow(row) {
    const track = row.querySelector('.collection-row__track');
    if (track) {
      Array.from(track.children).forEach((item) => {
        row.before(item);
        this.restoreRowFormat(item);
      });
    }
    row.remove();
  }

  isRowVisible(item) {
    return !item.classList.contains('product-out-of-stock') && !item.classList.contains('preloaded-hidden');
  }

  moveIntoRow(row, item) {
    // Dentro de la fila todas las cards son de una columna: los formatos dobles/panorámicos
    // no aplican (el original queda en data-original-format y se restaura si sale).
    if (!item.dataset.originalFormat) {
      item.dataset.originalFormat = item.getAttribute('data-format') || 'vertical_actual';
    }
    item.setAttribute('data-format', 'vertical_actual');
    item.classList.remove('collection__grid__item--orphan');
    this.clearInlineStyles(item);
    row.querySelector('.collection-row__track').appendChild(item);
    this.resumeVideos(item);
  }

  restoreRowFormat(item) {
    if (item.dataset.originalFormat) item.setAttribute('data-format', item.dataset.originalFormat);
    this.resumeVideos(item);
  }

  // Mover un <video> en el DOM lo pausa; los autoplay (mudos) se reanudan.
  resumeVideos(item) {
    item.querySelectorAll('video[autoplay]').forEach((video) => {
      if (video.paused) video.play().catch(() => {});
    });
  }

  clearInlineStyles(item) {
    item.removeAttribute('style');

    const brn_card = item.querySelector('.brn-card');
    const card_container = item.querySelector('.card__container');

    if (brn_card) {
      brn_card.removeAttribute('style');
    }

    if (card_container) {
      card_container.removeAttribute('style');
    }
  }

  applyVerticalGrandeFormat(item) {
    // Cambiar el data-format para que el CSS lo maneje
    item.setAttribute('data-format', 'vertical_grande');
    this.clearInlineStyles(item);
  }

  applyPanoramicaActualFormat(item) {
    // El CSS ya maneja esto con data-format="panoramica_actual"
    this.clearInlineStyles(item);
  }

  applyPanoramicaGrandeFormat(item) {
    // El CSS ya maneja esto con data-format="panoramica_grande"
    this.clearInlineStyles(item);
  }
}

/**
 * Fila `row_grid` (la crea CollectionGridLayout.groupRows). En modo slider (lo decide el CSS
 * según data-row-size y viewport) el track hace scroll horizontal nativo con snap: swipe con
 * el dedo, flechas, y arrastre con ratón en desktop. Las flechas (sobre las cards, a la
 * derecha) solo se muestran cuando se puede deslizar hacia su lado.
 */
if (!customElements.get('collection-row')) {
  customElements.define(
    'collection-row',
    class CollectionRow extends HTMLElement {
      connectedCallback() {
        if (this._initialized) return;
        this._initialized = true;

        this._track = this.querySelector('.collection-row__track');
        this._prev = this.querySelector('.collection-row__arrow--prev');
        this._next = this.querySelector('.collection-row__arrow--next');
        if (!this._track) return;

        this._track.addEventListener(
          'scroll',
          () => {
            if (this._raf) return;
            this._raf = requestAnimationFrame(() => {
              this._raf = null;
              this.refresh();
            });
          },
          { passive: true }
        );

        this._prev?.addEventListener('click', () => this.step(-1));
        this._next?.addEventListener('click', () => this.step(1));

        this._bindMouseDrag();

        this._resizeObserver = new ResizeObserver(() => this.refresh());
        this._resizeObserver.observe(this._track);
      }

      disconnectedCallback() {
        // La fila no se mueve nunca; si sale del DOM es porque facets reemplazó el grid.
        this._resizeObserver?.disconnect();
      }

      // Avanza/retrocede una card.
      step(direction) {
        const slide = this._slideWidth();
        if (!slide) return;
        // Con clics seguidos se encadena desde el destino en curso, no desde la posición a medio camino.
        const from = this._animTarget ?? this._track.scrollLeft;
        let target = Math.round(from / slide + direction) * slide;
        // Con la card asomando, el final no cae en múltiplo de card: si solo queda ese resto,
        // se va directo al final en vez de dejar una pasada de media card.
        const maxScroll = this._track.scrollWidth - this._track.clientWidth;
        if (target > maxScroll - slide / 2) target = maxScroll;
        this.animateTo(target);
      }

      _slideWidth() {
        const first = Array.from(this._track.children).find((el) => el.offsetParent !== null);
        if (!first) return 0;
        const gap = parseFloat(getComputedStyle(this._track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }

      // Desplazamiento con easing propio (el `behavior: 'smooth'` nativo es seco y su duración
      // no se puede ajustar). El snap se desactiva mientras dura para que no pelee con los frames.
      animateTo(left) {
        const track = this._track;
        const target = Math.max(0, Math.min(left, track.scrollWidth - track.clientWidth));
        this._stopAnimation();

        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          track.scrollLeft = target;
          return;
        }

        const start = track.scrollLeft;
        const distance = target - start;
        const duration = 550;
        const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
        let startTime = null;

        this._animTarget = target;
        track.classList.add('is-animating');

        const tick = (now) => {
          startTime ??= now;
          const t = Math.min((now - startTime) / duration, 1);
          track.scrollLeft = start + distance * ease(t);
          if (t < 1) {
            this._animFrame = requestAnimationFrame(tick);
          } else {
            this._animTarget = null;
            track.classList.remove('is-animating');
          }
        };
        this._animFrame = requestAnimationFrame(tick);
      }

      _stopAnimation() {
        cancelAnimationFrame(this._animFrame);
        this._animTarget = null;
        this._track.classList.remove('is-animating');
      }

      refresh() {
        if (!this._track) return;
        const { scrollLeft, scrollWidth, clientWidth } = this._track;
        const maxScroll = scrollWidth - clientWidth;
        const scrollable = maxScroll > 1;

        this.classList.toggle('is-scrollable', scrollable);
        if (this._prev) this._prev.hidden = !scrollable || scrollLeft <= 1;
        if (this._next) this._next.hidden = !scrollable || scrollLeft >= maxScroll - 1;
      }

      // En táctil el scroll nativo ya hace el swipe; esto añade el arrastre con ratón.
      _bindMouseDrag() {
        const track = this._track;
        let startX = 0;
        let startScroll = 0;
        let dragging = false;
        let moved = false;

        track.addEventListener('pointerdown', (e) => {
          moved = false;
          this._stopAnimation();
          if (e.pointerType !== 'mouse' || e.button !== 0 || !this.classList.contains('is-scrollable')) return;
          dragging = true;
          startX = e.clientX;
          startScroll = track.scrollLeft;
        });

        track.addEventListener('pointermove', (e) => {
          if (!dragging) return;
          const dx = e.clientX - startX;
          if (!moved && Math.abs(dx) > 5) {
            moved = true;
            track.setPointerCapture(e.pointerId);
            track.classList.add('is-dragging');
          }
          if (moved) track.scrollLeft = startScroll - dx;
        });

        const end = (e) => {
          if (!dragging) return;
          dragging = false;
          if (!moved) return;
          track.classList.remove('is-dragging');
          if (track.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId);
          // El scroll-snap se desactiva durante el arrastre; al soltar se encaja en la card
          // más cercana.
          const slide = this._slideWidth();
          if (slide > 0) this.animateTo(Math.round(track.scrollLeft / slide) * slide);
        };
        track.addEventListener('pointerup', end);
        track.addEventListener('pointercancel', end);

        // Un arrastre no debe acabar abriendo el producto sobre el que se soltó.
        track.addEventListener(
          'click',
          (e) => {
            if (!moved) return;
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          },
          true
        );
        // Evita el "fantasma" de arrastre nativo de imágenes/enlaces.
        track.addEventListener('dragstart', (e) => {
          if (this.classList.contains('is-scrollable')) e.preventDefault();
        });
      }
    }
  );
}

// Inicializar
new CollectionGridLayout();

